import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import PaymentMethodDialog from "@/components/PaymentMethodDialog";
import PLANS from "@/content/plans.json";

export default function PricingPage() {
    const [interval, setInterval] = useState("monthly");
    const [dialog, setDialog] = useState(null); // {plan, lookup_key, amount, item_name}
    const { user } = useAuth();
    const navigate = useNavigate();

    const openPaymentDialog = (planKey) => {
        if (!user || user === false) {
            toast.info("Créez un compte pour vous abonner.");
            navigate("/inscription");
            return;
        }
        const plan = PLANS.find((p) => p.key === planKey);
        const lookup_key = `${planKey}_${interval === "monthly" ? "monthly" : "yearly"}`;
        const amount = (interval === "monthly" ? plan.monthly : plan.yearly) * 100;
        setDialog({
            lookup_key,
            item_name: `${plan.name} · ${interval === "monthly" ? "mensuel" : "annuel"}`,
            amount,
            kind: "subscription",
            origin_url: window.location.origin,
        });
    };

    // Called by PaymentMethodDialog. If a gift code was entered inside the popup,
    // it is passed here as opts.discount_code.
    const stripeCheckout = async (opts) => {
        const payload = {
            lookup_key: dialog.lookup_key,
            origin_url: window.location.origin,
        };
        if (opts?.discount_code) payload.discount_code = opts.discount_code;
        const { data } = await http.post("/payments/checkout", payload);
        window.location.href = data.checkout_url;
    };

    return (
        <div className="max-w-6xl mx-auto px-6 py-20" data-testid="pricing-page">
            <Seo
                title="Abonnements · Foundation of Light"
                description="Abonnements Tisseur, Messager et Alchimiste : séries d'ateliers offertes, -33 % sur les autres évènements et communauté privée de la Foundation of Light."
                path="/abonnements"
            />
            <div className="text-center mb-16">
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Abonnements</div>
                <h1 className="font-serif text-5xl sm:text-6xl mb-6">Choisissez votre chemin</h1>
                <p className="text-lg text-foreground/70 max-w-2xl mx-auto mb-4">
                    S'abonner à Foundation of Light, c'est bien plus qu'accéder à une série d'ateliers :
                    c'est <strong>soutenir activement la mission de diffusion</strong> de la Foundation et rendre
                    ce travail énergétique accessible à toutes celles et ceux qui cheminent vers plus de
                    conscience, de paix intérieure et de reliance.
                </p>
                <p className="text-lg text-foreground/70 max-w-2xl mx-auto mb-8">
                    Chaque abonnement inclut l'accès gratuit à sa série d'ateliers et -33% automatiques sur toutes les autres séries
                    (Harmonisation de l'être, Éveil de conscience, Libération de l'Être, Unification de l'âme…), tant que votre abonnement est actif.
                </p>
                <p className="text-sm text-muted-foreground max-w-2xl mx-auto mb-8">
                    Note : la remise ne s'applique pas aux soins de lumière, aux séances de guidance, ni aux accompagnements.
                </p>
            </div>

            <div className="max-w-3xl mx-auto mb-14 rounded-3xl border border-primary/30 bg-primary/5 p-6 md:p-7 flex gap-4 items-start" data-testid="reservation-email-notice">
                <div className="p-2.5 rounded-full bg-primary/15 shrink-0"><Sparkles className="w-5 h-5 text-primary" /></div>
                <div>
                    <h3 className="font-serif text-xl mb-2">Vos confirmations arrivent depuis <span className="text-primary">reservation@foundation-of-light.com</span></h3>
                    <p className="text-sm text-foreground/80 leading-relaxed">
                        Toutes vos confirmations d'inscription (ateliers, soins, séances de guidance) et vos liens Zoom vous seront envoyés depuis cette adresse.
                        Pour que ces emails arrivent bien dans votre boîte principale et pas dans les indésirables,
                        <strong> ajoutez <a href="mailto:reservation@foundation-of-light.com" className="underline decoration-primary/40 hover:decoration-primary">reservation@foundation-of-light.com</a> à votre carnet d'adresses </strong>
                        dès votre abonnement souscrit.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {PLANS.map((p, i) => (
                    <motion.div
                        key={p.key}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        className={`relative rounded-3xl border p-8 md:p-10 flex flex-col ${
                            p.highlight
                                ? "border-secondary bg-card shadow-2xl md:-mt-6"
                                : "border-border/60 bg-card"
                        }`}
                        data-testid={`plan-${p.key}`}
                    >
                        {p.highlight && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground text-xs uppercase tracking-widest px-4 py-1.5 rounded-full flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> Conscience 5D+
                            </div>
                        )}
                        <h3 className="font-serif text-4xl mb-2">{p.name}</h3>
                        <p className="text-sm text-muted-foreground mb-8">{p.subtitle}</p>
                        <div className="mb-8">
                            <span className="font-serif text-6xl">{interval === "monthly" ? p.monthly : p.yearly}€</span>
                            <span className="text-muted-foreground ml-2 text-sm">/ {interval === "monthly" ? "mois" : "an"}</span>
                        </div>
                        <ul className="space-y-3 mb-10 flex-1">
                            {p.features.map((f) => (
                                <li key={f} className="flex items-start gap-3 text-sm">
                                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {f}
                                </li>
                            ))}
                        </ul>
                        <Button
                            onClick={() => openPaymentDialog(p.key)}
                            className={`w-full rounded-full h-12 ${
                                p.highlight
                                    ? "bg-secondary text-secondary-foreground hover:scale-[1.02]"
                                    : "bg-primary text-primary-foreground hover:scale-[1.02]"
                            } transition-transform`}
                            data-testid={`subscribe-${p.key}`}
                        >
                            S'abonner
                        </Button>
                    </motion.div>
                ))}
            </div>

            <div className="flex justify-center mt-12">
                <div className="inline-flex bg-muted rounded-full p-1" data-testid="interval-toggle">
                    <button
                        onClick={() => setInterval("monthly")}
                        className={`px-6 py-2 rounded-full text-sm transition-colors ${interval === "monthly" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
                        data-testid="interval-monthly"
                    >
                        Mensuel
                    </button>
                    <button
                        onClick={() => setInterval("yearly")}
                        className={`px-6 py-2 rounded-full text-sm transition-colors ${interval === "yearly" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
                        data-testid="interval-yearly"
                    >
                        Annuel <span className="text-secondary ml-1">-2 mois</span>
                    </button>
                </div>
            </div>

            <div className="max-w-3xl mx-auto mt-14 rounded-3xl border border-secondary/30 bg-gradient-to-br from-secondary/10 to-transparent p-6 md:p-7 flex gap-4 items-start" data-testid="summer-pause-notice">
                <div className="p-2.5 rounded-full bg-secondary/20 shrink-0"><Sparkles className="w-5 h-5 text-secondary" /></div>
                <div>
                    <h3 className="font-serif text-xl mb-2">Une pause sacrée, un lien qui continue</h3>
                    <p className="text-sm text-foreground/80 leading-relaxed">
                        Les ateliers offerts par votre abonnement rythment le chemin <strong>de septembre à juin</strong>.
                        En <strong>juillet et août</strong>, ils observent une pause consciente : un temps
                        d'<em>intériorisation</em> offert à chacun·e pour laisser décanter, intégrer et se laisser
                        traverser par ce qui a été semé.
                    </p>
                    <p className="text-sm text-foreground/80 leading-relaxed mt-3">
                        Cette pause n'est pas un vide — c'est aussi, pour la Foundation of Light, un temps fort
                        de travail intérieur, une manière de se préparer en profondeur à l'élan de la rentrée
                        que vous retrouverez dès septembre.
                    </p>
                    <p className="text-sm text-foreground/80 leading-relaxed mt-3">
                        Votre soutien, lui, continue de vivre pendant ces deux mois. C'est lui qui rend cette
                        pause possible, et c'est lui aussi qui vous garde relié·e à ce qui continue de se
                        tisser durant l'été. La communauté reste active via <strong>le cercle</strong> —
                        vous y retrouvez des partages plus intimes, réservés à celles et ceux qui cheminent
                        dans la durée. Et parce que ce chemin continue de bien des
                        manières, <strong>d'autres ateliers ont lieu pendant la pause</strong>, auxquels votre
                        abonnement vous donne accès avec <strong>33 % de réduction</strong>.
                    </p>
                    <p className="text-sm text-foreground/80 leading-relaxed mt-3">
                        Rester abonné·e, c'est donc rester pleinement présent·e au chemin — même quand les
                        ateliers habituels se taisent.
                    </p>
                </div>
            </div>
            <PaymentMethodDialog
                open={!!dialog}
                onOpenChange={(o) => !o && setDialog(null)}
                item={dialog}
                stripeCheckout={stripeCheckout}
                allowGiftCode={true}
                giftCodeScope="subscription"
            />
        </div>
    );
}
